import {
  actions,
  KeyMap,
  List,
  Panel,
  TitledContainer,
} from '@infotech-soe/erp-ui-kit';
import { TPrivsCatalogsActionsLabels } from '@infotech-soe/merps/types/Privs';
import React, { memo, MouseEvent } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { defineMessages, useIntl } from 'react-intl';

import {
  IPrivsTreeAction,
  TPaneSign,
} from '../../../models/role-privs/RolePrivsData';

import PrivsActionsListItem from './PrivsActionsListItem';
import { TPrivsActionsConnectorProps } from './RolePrivsListActionContainer';
import { usePrivsPanelStyles } from './styles';

const messages = defineMessages({
  catalogCreate: {
    id: 'core.category.tree.catalogs.create',
    defaultMessage: 'Create catalog',
  },
  catalogUpdate: {
    id: 'core.category.tree.catalogs.rename',
    defaultMessage: 'Update catalog',
  },
  catalogMove: {
    id: 'core.category.tree.catalogs.move',
    defaultMessage: 'Move catalog',
  },
  catalogDelete: {
    id: 'core.category.tree.catalogs.delete',
    defaultMessage: 'Delete catalog',
  },
});

const categoryTitles = defineMessages({
  catalog: {
    id: 'core.rolePrivs.actions.title.catalogs',
    defaultMessage: 'Catalog actions',
  },
  category: {
    id: 'core.rolePrivs.actions.title.category',
    defaultMessage: 'Category actions',
  },
  hierarchy: {
    id: 'core.rolePrivs.actions.title.hierarchy',
    defaultMessage: 'Category actions in child nodes',
  },
});

const getActionTitle = (
  pane: TPaneSign,
  isCategoryTree?: boolean
): (typeof categoryTitles)[keyof typeof categoryTitles] => {
  if (pane === 'catalog' && !isCategoryTree) {
    return categoryTitles.catalog;
  } else if (pane === 'hierarchy') {
    return categoryTitles.hierarchy;
  } else {
    return categoryTitles.category;
  }
};

const PrivsActionsList: React.FC<TPrivsActionsConnectorProps> = props => {
  const {
    className,
    disabled,
    options: optionsProp,
    onChange,
    isCategoryTree,
    pane,
  } = props;
  const classes = usePrivsPanelStyles();
  const { formatMessage } = useIntl();
  const title = (
    <div className={classes.actionTitle}>
      {formatMessage(getActionTitle(pane, isCategoryTree))}
    </div>
  );

  const handleChange =
    (action: IPrivsTreeAction) =>
    (event: MouseEvent<HTMLDivElement> | KeyboardEvent) => {
      if (
        (event.currentTarget as HTMLElement)?.getAttribute('role') ===
        'listitem'
      ) {
        event.preventDefault();
        onChange(action);
        (event.currentTarget as HTMLElement).focus();
      }
    };

  const hotkeyRef = useHotkeys<HTMLDivElement>(
    KeyMap[actions.SELECT_OPTION],
    () => (document.activeElement as HTMLElement).click(),
    { enabled: !disabled }
  );

  const options = optionsProp?.map(action => ({
    children: (
      <PrivsActionsListItem
        action={{
          ...action,
          label: isCategoryTree
            ? action.label
            : action.label &&
              formatMessage(
                messages[action.label as TPrivsCatalogsActionsLabels]
              ),
        }}
        disabled={disabled}
      />
    ),
    disabled,
    name: action.actionCode,
    role: 'listitem',
    type: 'item' as const,
    onClick: handleChange(action),
  }));

  return (
    <Panel className={className}>
      <TitledContainer title={title}>
        {!!options?.length && (
          <List
            autoActivate
            className={'full-height ' + classes.container}
            focusOnActivation
            ref={hotkeyRef}
            withHotkeys
          >
            <List.Items options={options} />
          </List>
        )}
      </TitledContainer>
    </Panel>
  );
};

export default memo(PrivsActionsList);
